/**
 * Traducción de fallos a algo que se pueda leer y accionar.
 *
 * Regla del panel: cuando algo no está, la pantalla lo dice. Nunca se rellena
 * con datos de ejemplo ni se disfraza un 404 de «no hay roles configurados»:
 * quien opera la consola necesita distinguir «el sistema no tiene roles» de «la
 * consola no pudo preguntarlos», porque las dos cosas se arreglan distinto.
 */

import { ApiError } from '@/lib/api';
import { ContractError } from '@/panel/contract';

export interface FalloDescripto {
  title: string;
  message: string;
  detail?: string;
  /** `true` cuando lo que falta es el endpoint, no los datos. */
  faltaEndpoint: boolean;
}

export function describirError(error: unknown, ruta: string): FalloDescripto {
  if (error instanceof ContractError) {
    return {
      title: 'El servidor contestó en un formato inesperado',
      message: error.message,
      detail: ruta,
      faltaEndpoint: false,
    };
  }

  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        title: 'Todavía no hay de dónde leer los permisos',
        message:
          'El servicio que publica los roles no está disponible en este servidor. En cuanto exista, ' +
          'esta pantalla se llena sola: no hace falta tocar nada acá.',
        detail: `GET ${ruta} → 404`,
        faltaEndpoint: true,
      };
    }
    if (error.status === 403) {
      return {
        title: 'El servidor no te dejó ver esto',
        message: 'Tu usuario entró a la consola pero no tiene permiso para consultar los roles.',
        detail: `GET ${ruta} → 403`,
        faltaEndpoint: false,
      };
    }
    if (error.status === 0) {
      return {
        title: 'No se pudo contactar al servidor',
        message: 'Revisá que el backend esté levantado y volvé a intentar.',
        detail: ruta,
        faltaEndpoint: false,
      };
    }
    return {
      title: 'El servidor devolvió un error',
      message: error.message,
      detail: `GET ${ruta} → ${error.status}`,
      faltaEndpoint: false,
    };
  }

  return {
    title: 'Algo salió mal',
    message: error instanceof Error ? error.message : 'Error desconocido.',
    detail: ruta,
    faltaEndpoint: false,
  };
}
